import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Sélecteur de langue moderne et organisé
 * Dropdown élégant avec Français, English, ភាសាខ្មែរ (Khmer)
 */
const LanguageSelector = ({ compact = false, className = "" }) => {
  const { language, changeLanguage, languages = [] } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languageList =
    languages.length > 0
      ? languages
      : [
          { code: "fr", name: "Français", flag: "🇫🇷", nativeName: "Français" },
          { code: "en", name: "English", flag: "🇬🇧", nativeName: "English" },
          { code: "km", name: "Khmer", flag: "🇰🇭", nativeName: "ភាសាខ្មែរ" },
        ];

  const currentLanguage =
    languageList.find((lang) => lang.code === language) || languageList[0];

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-200 shadow-sm hover:shadow-md group ${
          compact ? "gap-1.5 px-2.5 py-1.5" : "gap-2 px-3 py-2"
        }`}
        aria-label="Sélectionner la langue"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className={compact ? "text-lg" : "text-xl"}>{currentLanguage.flag}</span>
        <span
          className={`text-sm font-medium text-gray-700 dark:text-gray-300 ${
            compact ? "hidden" : "hidden sm:inline-block"
          }`}
        >
          {currentLanguage.code.toUpperCase()}
        </span>
        <svg
          className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={`absolute right-0 mt-2 ${
            compact ? "w-48" : "w-56"
          } bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50 animate-slide-in-up`}
        >
          {/* Header du dropdown */}
          {!compact && (
            <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Langue / Language / ភាសា
              </p>
            </div>
          )}

          {/* Liste des langues */}
          <div className="py-2">
            {languageList.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-3 px-4 ${
                  compact ? "py-2.5" : "py-3"
                } text-left transition-all duration-200 ${
                  language === lang.code
                    ? "bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 border-l-4 border-indigo-500"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                }`}
              >
                <span className={`${compact ? "text-xl" : "text-2xl"} flex-shrink-0`}>
                  {lang.flag}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-semibold ${
                        language === lang.code
                          ? "text-indigo-700 dark:text-indigo-400"
                          : "text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {lang.nativeName}
                    </span>
                    {language === lang.code && (
                      <svg
                        className="w-4 h-4 text-indigo-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  {!compact && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {lang.name}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
